import type { Area } from 'react-easy-crop'

export const createImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image()
    image.addEventListener('load', () => resolve(image))
    image.addEventListener('error', (error) => {
      console.error('Image load failed:', url, error)
      reject(error)
    })
    image.setAttribute('crossOrigin', 'anonymous') // needed to avoid cross-origin issues on CodeSandbox
    image.src = url
  })
}


export function getRadianAngle(degreeValue: number) {
  return (degreeValue * Math.PI) / 180
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width: number, height: number, rotation: number) {
  const rotRad = getRadianAngle(rotation)

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  }
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
export async function getCroppedImg(
  imageSrc: string,
  pixelCrop: Area,
  rotation = 0,
  flip = { horizontal: false, vertical: false }
): Promise<HTMLCanvasElement | null> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return null
  }

  const rotRad = getRadianAngle(rotation)

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation
  )

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth
  canvas.height = bBoxHeight

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2)
  ctx.rotate(rotRad)
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1)
  ctx.translate(-image.width / 2, -image.height / 2)

  // draw rotated image
  ctx.drawImage(image, 0, 0)

  const croppedCanvas = document.createElement('canvas')

  const croppedCtx = croppedCanvas.getContext('2d')

  if (!croppedCtx) {
    return null
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width
  croppedCanvas.height = pixelCrop.height

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  )

  return croppedCanvas
  // As Base64 string
  // return croppedCanvas.toDataURL('image/jpeg');

  // As a blob
  // return new Promise((resolve, reject) => {
  //   croppedCanvas.toBlob((file) => {
  //     if (!file) {
  //       return reject(new Error('Failed to create blob'))
  //     }
  //     let fileSrc = URL.createObjectURL(file)
  //     resolve(fileSrc)
  //   }, 'image/png')
  // })
}


// 将图片绘制到目标画布的指定位置
export const drawImageToTargetCanvas = async (
  canvasSize: { width: number; height: number },
  sourceImagePath: string | undefined,
  targetCanvas: HTMLCanvasElement,
  targetPosition?: { x: number; y: number }
): Promise<HTMLCanvasElement> => {
  const canvas = document.createElement('canvas')
  const canvasCtx = canvas.getContext('2d')

  if (!canvasCtx) {
    throw new Error('Target canvas context not available')
  }

  canvas.width = canvasSize.width
  canvas.height = canvasSize.height

  // 清空目标画布（透明背景）
  canvasCtx.clearRect(0, 0, canvas.width, canvas.height)

  // 如果提供了源图片路径，先绘制源图片（按比例缩放到 canvas 大小）
  if (sourceImagePath) {
    const sourceImage = await createImage(sourceImagePath)

    // 计算缩放比例（保持宽高比）
    const scaleX = canvas.width / sourceImage.width
    const scaleY = canvas.height / sourceImage.height
    const scale = Math.min(scaleX, scaleY)

    const scaledWidth = sourceImage.width * scale
    const scaledHeight = sourceImage.height * scale

    // 计算居中位置
    const sourcePosX = (canvas.width - scaledWidth) / 2
    const sourcePosY = (canvas.height - scaledHeight) / 2

    // 绘制缩放后的源图片
    canvasCtx.drawImage(sourceImage, sourcePosX, sourcePosY, scaledWidth, scaledHeight)
  }

  // 计算绘制位置（默认居中）
  const posX = targetPosition?.x ?? (canvas.width - targetCanvas.width) / 2
  const posY = targetPosition?.y ?? (canvas.height - targetCanvas.height) / 2

  // 将裁剪后的图片绘制到目标画布的指定位置
  canvasCtx.drawImage(targetCanvas, posX, posY)

  return canvasCtx.canvas
}

export async function getRotatedImage(imageSrc: string, rotation = 0): Promise<string | null> {
  const image = await createImage(imageSrc)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')

  if (!ctx) {
    return null
  }

  const orientationChanged =
    rotation === 90 || rotation === -90 || rotation === 270 || rotation === -270
  if (orientationChanged) {
    canvas.width = image.height
    canvas.height = image.width
  } else {
    canvas.width = image.width
    canvas.height = image.height
  }

  ctx.translate(canvas.width / 2, canvas.height / 2)
  ctx.rotate((rotation * Math.PI) / 180)
  ctx.drawImage(image, -image.width / 2, -image.height / 2)

  return new Promise((resolve) => {
    canvas.toBlob((file) => {
      if (!file) {
        return resolve(null)
      }
      resolve(URL.createObjectURL(file))
    }, 'image/png')
  })
}


// 保存图片到磁盘
export const saveCanvasToDisk = (dataCanvas: HTMLCanvasElement, filename: string = 'cropped-image.png') => {

  const dataUrl = dataCanvas.toDataURL('image/png')
  saveImageToDisk(dataUrl, filename)
}

export const saveImageToDisk = (dataUrl: string, filename: string = 'cropped-image.png') => {

  const link = document.createElement('a')
  link.href = dataUrl
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}